import { useEffect, useState } from "react";
import { PlusCircle, Pencil, Trash2, Search, X } from "lucide-react";
import api, { fmtTRY, fmtDateTime } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTION_META = {
  create: { label: "Oluşturma", icon: PlusCircle, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  update: { label: "Düzenleme", icon: Pencil, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  delete: { label: "Silme", icon: Trash2, cls: "bg-red-50 text-red-700 border-red-200" },
};

const FIELD_LABELS = {
  amount: "Tutar", description: "Açıklama", expense_date: "Tarih", supplier: "Tedarikçi",
  payment_method: "Ödeme", observations: "Gözlemler", trade_name: "İş Kalemi", trade_id: "İş Kalemi",
  name: "İsim",
};

const fmtVal = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "amount") return fmtTRY(v);
  return String(v);
};

const emptyF = { action: "", user_id: "", trade_id: "", search: "", date_from: "", date_to: "" };

export default function History() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [trades, setTrades] = useState([]);
  const [f, setF] = useState(emptyF);

  const load = () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/history", { params }).then((r) => setLogs(r.data));
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/trades").then((r) => setTrades(r.data));
  }, []);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [f]);

  const activeCount = Object.values(f).filter(Boolean).length;

  return (
    <div className="animate-fade-up">
      <PageHeader overline="Denetim İzi" title="Geçmiş" subtitle="Tüm oluşturma, düzenleme ve silme işlemleri" />

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
          <Label className="text-xs">Ara</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={f.search} onChange={(e) => setF((s) => ({ ...s, search: e.target.value }))} placeholder="Gider / yorum / kullanıcı" className="pl-9" data-testid="history-search" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">İşlem</Label>
          <Select value={f.action || "all"} onValueChange={(v) => setF((s) => ({ ...s, action: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="history-action-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="create">Oluşturma</SelectItem>
              <SelectItem value="update">Düzenleme</SelectItem>
              <SelectItem value="delete">Silme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Kullanıcı</Label>
          <Select value={f.user_id || "all"} onValueChange={(v) => setF((s) => ({ ...s, user_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="history-user-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">İş Kalemi</Label>
          <Select value={f.trade_id || "all"} onValueChange={(v) => setF((s) => ({ ...s, trade_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="history-trade-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Başlangıç</Label><Input type="date" value={f.date_from} onChange={(e) => setF((s) => ({ ...s, date_from: e.target.value }))} data-testid="history-date-from" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Bitiş</Label><Input type="date" value={f.date_to} onChange={(e) => setF((s) => ({ ...s, date_to: e.target.value }))} data-testid="history-date-to" /></div>
        {activeCount > 0 && (
          <div className="flex items-end">
            <Button variant="ghost" onClick={() => setF(emptyF)} data-testid="history-clear"><X className="mr-1 h-4 w-4" /> Temizle</Button>
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="history-list">
        {logs.length === 0 && <div className="rounded-md border border-border bg-card py-12 text-center text-sm text-muted-foreground">Kayıt bulunamadı</div>}
        {logs.map((log) => {
          const meta = ACTION_META[log.action] || ACTION_META.update;
          const Icon = meta.icon;
          return (
            <div key={log.id} className="rounded-md border border-border bg-card p-4" data-testid={`history-item-${log.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="outline" className={`gap-1 ${meta.cls}`}><Icon className="h-3 w-3" /> {meta.label}</Badge>
                <span className="text-sm font-medium">{log.expense_description}</span>
                {log.expense_amount != null && <span className="text-sm font-semibold tabular-nums">{fmtTRY(log.expense_amount)}</span>}
                <span className="ml-auto text-xs text-muted-foreground">{fmtDateTime(log.timestamp)}</span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{log.user_name}</span>
                {log.trade_name && <> · {log.trade_name}</>}
                {log.comment && <> · "{log.comment}"</>}
              </div>
              {log.action === "update" && log.changes?.length > 0 && (
                <div className="mt-3 space-y-1 rounded-md bg-secondary/50 p-3 text-xs">
                  {log.changes.map((c, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{FIELD_LABELS[c.field] || c.field}:</span>
                      <span className="text-muted-foreground line-through">{fmtVal(c.field, c.old)}</span>
                      <span>→</span>
                      <span className="font-medium">{fmtVal(c.field, c.new)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
